import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  SITE_NAME, DEFAULT_IMAGE, WEBSITE_ID, absUrl, organizationRef, breadcrumbSchema,
} from "@/lib/seo";

const setMeta = (attr, key, content) => {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

/**
 * Per-page SEO: title, description, canonical, robots, Open Graph, Twitter and JSON-LD.
 * useSeo(title, description, { noindex, image, type, breadcrumbs: [{name, path}], schema: [nodes], pageType })
 */
export default function useSeo(title, description, options = {}) {
  const { pathname } = useLocation();
  const {
    noindex = false, image, type = "website", breadcrumbs, schema = [], pageType = "WebPage",
  } = options;
  const schemaKey = JSON.stringify([schema, breadcrumbs, pageType]);

  useEffect(() => {
    const url = absUrl(pathname);
    const img = image || DEFAULT_IMAGE;
    if (title) document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    setLink("canonical", url);
    setMeta("property", "og:type", type);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:site", "@Serpens_Tech");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);

    const old = document.getElementById("page-jsonld");
    if (old) old.remove();
    if (noindex) return;
    const graph = [
      {
        "@type": pageType,
        "@id": `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { "@id": WEBSITE_ID },
        about: organizationRef,
        inLanguage: "en-IN",
        ...(breadcrumbs ? { breadcrumb: { "@id": `${url}#breadcrumb` } } : {}),
      },
      ...(breadcrumbs ? [{ ...breadcrumbSchema(breadcrumbs), "@id": `${url}#breadcrumb` }] : []),
      ...schema,
    ];
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "page-jsonld";
    s.text = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, noindex, pathname, image, type, schemaKey]);
}
